import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import { waitForElement, suppressAzPopup, clearAzPopup } from "./guideUtils";
import GuidePopup from "./GuidePopup";

// Step 3 of the /home roadmap: "Add a product to favorites".
// Triggered by arriving on /retail?guide=favorites.
//
// Walkthrough (desktop = react-joyride, mobile = "Got it" popups):
//   1. Heart icon (.favHeartIcon)  -> "click it to favorite a product".
//   2. Navbar "Dashboard" link      -> "open your Dashboard".
//   3. Favorites "Load" (#favLoadBtn) -> "load to view & export your favorites".
//
// Advancing step 1 happens on either a real heart click OR the tooltip button.
// The tour spans a page change (/retail -> /dashboard); this component lives
// inside the Router and stays mounted across that client-side navigation.

// Visual style matched to the app's other Joyride tours.
const JOYRIDE_STYLES = {
  spotlight: { borderRadius: 20 },
  tooltip: { padding: "3%" },
  tooltipContainer: { textAlign: "center" },
  tooltipTitle: { fontSize: "110%", padding: "3% 1% 1% 1%" },
  tooltipContent: { fontSize: "130%", padding: "3% 3% 2% 3%" },
  options: {
    arrowColor: "#E3D0DF",
    backgroundColor: "#E3D0DF",
    primaryColor: "#8c3e7c",
    textColor: "black",
    zIndex: 1000,
  },
};

const DESKTOP_STEPS = [
  {
    target: ".favHeartIcon",
    title: "Add a product to favorites",
    content:
      "Click this heart to mark a product as a favorite. Favorites are saved so you can find them again anytime.",
    disableBeacon: true,
    disableScrolling: true,
    spotlightClicks: true,
  },
  {
    target: 'a[href="/dashboard"]',
    title: "Open your Dashboard",
    content: "Now click \"Dashboard\" to see your saved favorites.",
    disableBeacon: true,
    disableScrolling: true,
    spotlightClicks: true,
  },
  {
    target: "#favLoadBtn",
    title: "View & export your favorites",
    content:
      "Click \"Load\" here to view all the products you favorited — from here you can also export them.",
    disableBeacon: true,
    disableScrolling: true,
    spotlightClicks: true,
  },
];

function FavoritesGuideTrigger({ isMobile, isAuthenticated, isLoading }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Mobile: 0 = off, 1 = heart, 2 = dashboard, 3 = load
  const [mobileStep, setMobileStep] = useState(0);

  const startedRef = useRef(false);
  const awaitDashboardRef = useRef(false); // mobile: waiting for user to reach /dashboard
  // Mirrors for event handlers (avoid stale closures).
  const runRef = useRef(false);
  runRef.current = run;
  const stepIndexRef = useRef(0);
  stepIndexRef.current = stepIndex;

  const endGuide = () => {
    setRun(false);
    setStepIndex(0);
    startedRef.current = false;
    clearAzPopup();
  };

  // Advance heart-step -> dashboard-step. If the navbar Dashboard link is
  // hidden (Bulma collapses .navbar-menu below 1024px while our isMobile
  // breakpoint is 700px), skip the spotlight and take them there directly.
  const advanceToDashboardStep = () => {
    const link = document.querySelector('a[href="/dashboard"]');
    if (link && link.offsetParent !== null) {
      setStepIndex(1);
    } else {
      navigate("/dashboard"); // /dashboard effect advances to the Load step
    }
  };

  // --- Start the guide on /retail?guide=favorites ---
  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get("guide") !== "favorites") return;
    if (location.pathname !== "/retail") return;
    if (isLoading || !isAuthenticated) return;

    startedRef.current = true;
    // Keep the auto Amazon-sync popup from covering the spotlight mid-guide.
    suppressAzPopup();
    // Strip the param from the URL bar only (not the router location) so it
    // won't disturb the page / joyride mid-spotlight.
    window.history.replaceState({}, "", "/retail");

    if (isMobile) {
      setMobileStep(1);
      return;
    }

    // Desktop: wait for a heart to mount, then start the spotlight.
    const cancel = waitForElement(".favHeartIcon", {
      tries: 40,
      interval: 500,
      onFound: () => {
        setStepIndex(0);
        setRun(true);
      },
      onTimeout: () => {
        // No heart ever mounted — give up, and un-suppress the Amazon-sync popup
        // we silenced above. Without this it stays suppressed for the rest of the
        // visit (the guide is over; nothing else clears the flag on this path).
        startedRef.current = false;
        clearAzPopup();
      },
    });
    return () => cancel();
  }, [location, isMobile, isAuthenticated, isLoading, navigate]);

  // Leaving the guide's pages (it legitimately hops /retail -> /dashboard)
  // fully ends an abandoned tour so it can't resurface on unrelated pages,
  // and re-arms it for a later roadmap click.
  useEffect(() => {
    if (location.pathname !== "/retail" && location.pathname !== "/dashboard") {
      setRun(false);
      setStepIndex(0);
      setMobileStep(0);
      startedRef.current = false;
      clearAzPopup();
    }
  }, [location.pathname]);

  // --- Desktop: advance to step 1 when the heart is actually clicked ---
  useEffect(() => {
    if (isMobile) return;
    const onFav = () => {
      if (runRef.current && stepIndexRef.current === 0) advanceToDashboardStep();
    };
    window.addEventListener("nepeto:favoriteClicked", onFav);
    return () => window.removeEventListener("nepeto:favoriteClicked", onFav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // --- Advance to the Load step once we land on /dashboard ---
  useEffect(() => {
    if (location.pathname !== "/dashboard") return;

    // Mobile: show the final popup.
    if (isMobile) {
      if (awaitDashboardRef.current) {
        awaitDashboardRef.current = false;
        setMobileStep(3);
      }
      return;
    }

    // Desktop: wait for the Load button, then spotlight it.
    if (!run) return;
    const cancel = waitForElement("#favLoadBtn", {
      tries: 30,
      interval: 300,
      requireVisible: false,
      onFound: () => setStepIndex(2),
    });
    return () => cancel();
  }, [location, isMobile, run]);

  const handleJoyride = (data) => {
    const { action, index, status, type } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
      endGuide();
      return;
    }
    // Clicking "Load" (last step) unmounts #favLoadBtn -> joyride can't find the
    // target. Treat that as done, else `run` stays true and the tour re-fires on
    // later /dashboard visits.
    // (Also fires if the tour is abandoned and the current target unmounts.)
    if (type === EVENTS.TARGET_NOT_FOUND) {
      endGuide();
      return;
    }
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      if (index === 0) {
        advanceToDashboardStep();
      } else if (index === 1) {
        // "Got it" without clicking the link — take them there ourselves.
        navigate("/dashboard");
        // The /dashboard effect advances to step 2 once the button mounts.
      } else if (index === 2) {
        endGuide();
      }
    }
  };

  // ---------- Render ----------
  if (!isMobile) {
    if (!run) return null;
    return (
      <Joyride
        steps={DESKTOP_STEPS}
        run={run}
        stepIndex={stepIndex}
        continuous={true}
        disableCloseOnEsc={true}
        disableOverlayClose={true}
        hideCloseButton={true}
        showSkipButton={false}
        spotlightClicks={true}
        callback={handleJoyride}
        locale={{ last: "Got it", next: "Got it" }}
        styles={JOYRIDE_STYLES}
      />
    );
  }

  // Mobile popups
  if (mobileStep === 0) return null;

  let tip = null;
  let onGotIt = () => setMobileStep(0);
  if (mobileStep === 1) {
    tip = (
      <>
        Tap the <strong style={{ color: "#8c3e7c" }}>heart</strong> icon on any product
        to mark it as a favorite.
      </>
    );
    onGotIt = () => setMobileStep(2);
  } else if (mobileStep === 2) {
    tip = (
      <>
        Now tap the <strong style={{ color: "#8c3e7c" }}>☰ menu</strong> at the top, then{" "}
        <strong style={{ color: "#8c3e7c" }}>Dashboard</strong>, to view your favorites.
      </>
    );
    onGotIt = () => {
      awaitDashboardRef.current = true;
      setMobileStep(0);
    };
  } else if (mobileStep === 3) {
    tip = (
      <>
        Tap <strong style={{ color: "#8c3e7c" }}>Load</strong> under{" "}
        <strong style={{ color: "#8c3e7c" }}>Favorites</strong> to view and export the
        products you saved.
      </>
    );
    onGotIt = () => setMobileStep(0);
  }

  return <GuidePopup onDismiss={onGotIt}>{tip}</GuidePopup>;
}

export default FavoritesGuideTrigger;
